import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead, FAQSchema } from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { GraduationCap, Award, Users, Building, Calendar, MapPin, CheckCircle, ArrowRight, History } from 'lucide-react';

const ArtSchoolSanAntonio = () => {
  const faqs = [
    {
      question: "What makes Coppini Academy different from other art schools in San Antonio?",
      answer: "The Coppini Academy of Fine Art is a nonprofit institution focused exclusively on classical and representational art training. Unlike hobby studios, we offer structured curriculum based on atelier methods used by master painters for centuries."
    },
    {
      question: "Is the Coppini Academy accredited?",
      answer: "The Coppini Academy is a nonprofit 501(c)(3) educational organization dedicated to preserving and teaching classical fine art traditions. We focus on practical skill development rather than degree programs."
    },
    {
      question: "What age groups do you teach?",
      answer: "We offer programs for youth (ages 8+) through our Coppini Kids program, as well as comprehensive adult education including classes, workshops, and open studio sessions."
    },
    {
      question: "How long has the academy been in San Antonio?",
      answer: "The Coppini Academy of Fine Art has been championing classical art education in San Antonio since 1945, making us one of the oldest fine art institutions in Texas."
    }
  ];

  return (
    <main className="min-h-screen bg-white">
      <SEOHead
        title="Art School San Antonio | Fine Art Academy TX"
        description="The Coppini Academy of Fine Art - San Antonio's premier art school since 1945. Classical training in drawing, painting, and sculpture. Nonprofit fine art education for all ages."
        keywords="art school San Antonio, fine art academy San Antonio TX, art academy Texas, art classes San Antonio, fine art school, art education San Antonio"
        canonicalPath="/art-school-san-antonio"
      />
      <FAQSchema faqs={faqs} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 text-white py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-1 bg-cafa-gold/20 text-cafa-gold text-sm font-medium rounded-full mb-6">
              Est. 1945 • Nonprofit 501(c)(3)
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 leading-tight">
              San Antonio&apos;s Premier Fine Art Academy
            </h1>
            <p className="text-xl text-stone-300 mb-8 leading-relaxed">
              The Coppini Academy of Fine Art has been the heart of classical art education 
              in San Antonio for nearly 80 years. We offer rigorous training in drawing, 
              painting, and sculpture for serious students of all ages.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                <Link to="/classes/adult">Explore Our Programs <ArrowRight className="ml-2" size={18} /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Link to="/about">Our History</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-cafa-red py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {[
              { number: "1945", label: "Founded" },
              { number: "79+", label: "Years of Excellence" },
              { number: "800+", label: "Active Members" },
              { number: "50+", label: "Weekly Classes" }
            ].map((stat, idx) => (
              <div key={idx}>
                <p className="text-3xl md:text-4xl font-bold">{stat.number}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About the Academy */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-6">
                A Legacy of Artistic Excellence in San Antonio
              </h2>
              <p className="text-lg text-stone-600 mb-4 leading-relaxed">
                Named after renowned sculptor Pompeo Coppini, whose works grace the Texas State 
                Capitol and Alamo Cenotaph, The Coppini Academy of Fine Art continues his legacy 
                of artistic excellence in the heart of San Antonio.
              </p>
              <p className="text-lg text-stone-600 mb-6 leading-relaxed">
                As a nonprofit institution, we&apos;re committed to making quality art education 
                accessible to our community. Our mission is to preserve and promote classical 
                and representational fine art through education, exhibitions, and community engagement.
              </p>
              <div className="space-y-3">
                {[
                  "Nonprofit 501(c)(3) organization",
                  "Classical atelier-style curriculum",
                  "Professional artist instructors",
                  "Historic Monte Vista location",
                  "Programs for youth and adults"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center">
                    <CheckCircle className="text-cafa-red mr-3 flex-shrink-0" size={20} />
                    <span className="text-stone-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <Card className="border-stone-200">
                <CardContent className="p-6">
                  <History className="text-cafa-red mb-4" size={32} />
                  <h3 className="text-xl font-semibold text-stone-900 mb-2">Our Heritage</h3>
                  <p className="text-stone-600">
                    Founded in 1945 by artists dedicated to preserving classical training 
                    methods, we&apos;ve educated generations of San Antonio artists.
                  </p>
                  <Link to="/about/history" className="text-cafa-red hover:underline text-sm mt-3 inline-block">
                    Learn Our History →
                  </Link>
                </CardContent>
              </Card>
              <Card className="border-stone-200">
                <CardContent className="p-6">
                  <Award className="text-cafa-red mb-4" size={32} />
                  <h3 className="text-xl font-semibold text-stone-900 mb-2">Our Mission</h3>
                  <p className="text-stone-600">
                    To champion classical and representational fine art through education, 
                    providing accessible training for artists at all levels.
                  </p>
                  <Link to="/about" className="text-cafa-red hover:underline text-sm mt-3 inline-block">
                    About Us →
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Overview */}
      <section className="py-16 lg:py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4">
              Art Education Programs in San Antonio
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Whether you&apos;re a beginner or professional, we have programs designed 
              to develop your artistic skills through classical training methods.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: GraduationCap,
                title: "Adult Classes",
                desc: "Structured courses in drawing, painting, and sculpture for students 18 and older. Day and evening options available.",
                link: "/classes/adult",
                cta: "View Adult Classes"
              },
              {
                icon: Users,
                title: "Youth Programs",
                desc: "Coppini Kids offers age-appropriate classical art training for young artists ages 8-17.",
                link: "/classes/kids",
                cta: "Youth Programs"
              },
              {
                icon: Calendar,
                title: "Workshops",
                desc: "Intensive multi-day workshops with visiting master artists from across the country.",
                link: "/workshops",
                cta: "Upcoming Workshops"
              },
              {
                icon: Building,
                title: "Open Studio",
                desc: "Practice figure drawing from live models in our supervised open studio sessions.",
                link: "/classes/open-sessions",
                cta: "Open Sessions"
              },
              {
                icon: Award,
                title: "Membership",
                desc: "Join our community of artists with discounted classes, studio access, and exhibition opportunities.",
                link: "/membership",
                cta: "Become a Member"
              },
              {
                icon: MapPin,
                title: "Visit Us",
                desc: "Tour our historic Monte Vista studio and learn about our programs in person.",
                link: "/contact",
                cta: "Plan Your Visit"
              }
            ].map((program, idx) => (
              <Card key={idx} className="border-stone-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <program.icon className="text-cafa-red mb-4" size={36} />
                  <h3 className="text-xl font-semibold text-stone-900 mb-2">{program.title}</h3>
                  <p className="text-stone-600 mb-4">{program.desc}</p>
                  <Link to={program.link} className="text-cafa-red hover:underline font-medium">
                    {program.cta} →
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Classical Training */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4">
              Why Choose Classical Art Training?
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Classical training provides the foundational skills that have produced 
              master artists for centuries. Here&apos;s what sets our approach apart.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Structured Curriculum",
                desc: "Our program follows a logical progression from fundamentals to advanced techniques. Each skill builds upon the previous, ensuring solid development."
              },
              {
                title: "Emphasis on Observation",
                desc: "We train your eye to see accurately—the essential skill underlying all representational art. Learn to observe value, proportion, and color relationships."
              },
              {
                title: "Time-Tested Methods",
                desc: "Our teaching methods descend from the great ateliers of Europe. These approaches have produced master artists for generations."
              },
              {
                title: "Personal Attention",
                desc: "Small class sizes ensure you receive individual instruction and feedback. Our instructors are committed to your artistic growth."
              }
            ].map((item, idx) => (
              <div key={idx} className="flex">
                <CheckCircle className="text-cafa-red mr-4 mt-1 flex-shrink-0" size={24} />
                <div>
                  <h3 className="text-xl font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 lg:py-20 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif mb-6">
                Visit Our San Antonio Campus
              </h2>
              <p className="text-stone-300 mb-6 leading-relaxed">
                Located in the historic Monte Vista neighborhood, our academy occupies a 
                beautifully restored building with spacious studios, excellent natural lighting, 
                and an inspiring atmosphere for artistic creation.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <MapPin className="text-cafa-red mr-3 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-medium">The Coppini Academy of Fine Art</p>
                    <p className="text-stone-400">115 Melrose Place</p>
                    <p className="text-stone-400">San Antonio, TX 78212</p>
                    <p className="text-stone-400 mt-2">Phone: (210) 530-8445</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-cafa-red hover:bg-cafa-red-900">
                  <Link to="/contact">Get Directions</Link>
                </Button>
                <Button asChild variant="outline" className="border-white text-white hover:bg-white/10">
                  <Link to="/about">Learn More About Us</Link>
                </Button>
              </div>
            </div>
            <div className="bg-stone-800 rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3473.8!2d-98.4788!3d29.4649!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjnCsDI3JzUzLjYiTiA5OMKwMjgnNDMuNyJX!5e0!3m2!1sen!2sus!4v1"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Coppini Academy Location"
                className="grayscale"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border-b border-stone-200 pb-6">
                <h3 className="text-lg font-semibold text-stone-900 mb-2">{faq.question}</h3>
                <p className="text-stone-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-cafa-red text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-serif mb-4">
            Begin Your Artistic Journey
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join San Antonio&apos;s most respected fine art academy and discover your potential.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-cafa-red hover:bg-stone-100">
              <Link to="/classes/adult">Browse Classes</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <Link to="/membership">Become a Member</Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ArtSchoolSanAntonio;
